import {
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ErrorMessage } from "@hookform/error-message";
import {
  useCreateTestDiscount,
  useDeleteTestDiscount,
  useLabTests,
  useTestDiscounts,
} from "api/tests";
import Discount from "classes/discount";
import { add } from "date-fns";
import { getFormattedStringDate } from "helpers";
import EditIcon from "../../assets/svg/EditIcon";
import DeleteIcon from "../../assets/svg/DeleteIcon";
import { FaPlus as AddIcon } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useCategoryDiscounts,
  useCreateCategoryDiscount,
  useDeleteCategoryDiscount,
} from "api/categories";
import { getBaseTimeStamp } from "helpers/timezoneHelper";
import { getCategoryFee } from "modules/categories/CategoriesPage";

export const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
export function DiscountSection({
  test,
  category,
  showDiscounts = false,
  newDiscount,
  discount,
  onClose,
  editDiscount,
  action: initialAction,
}: {
  test?: any;
  category?: any;
  showDiscounts: boolean;
  newDiscount?: Function | undefined;
  discount?: Discount;
  editDiscount?: Function | undefined;
  onClose: Function | undefined;
  action?: String | undefined;
}) {
  const { handleSubmit, register, control, watch, errors, setValue } =
    useForm();
  const getDiscountF = test ? useTestDiscounts : useCategoryDiscounts;
  const createDiscountF = test
    ? useCreateTestDiscount
    : useCreateCategoryDiscount;
  const deleteDiscountF = test
    ? useDeleteTestDiscount
    : useDeleteCategoryDiscount;

  const discountsQuery = getDiscountF(test?.id || category?.id);
  const [createDiscount, createDiscountInfo] = createDiscountF();
  const [deleteDiscount, deleteDiscountInfo] = deleteDiscountF();
  const labTests = useLabTests();

  let [selectedDiscount, setSelectedDiscount] = useState<any>(
    discount || new Discount({})
  );

  const watchStartTime = watch("startTime");
  const [action, setAction] = useState<any>(initialAction);
  const [isFlatDiscount, setIsFlatDiscount] = useState<boolean>(false);

  useEffect(() => {
    if (discount) {
      setSelectedDiscount(discount);
      setIsFlatDiscount(discount?.flatDiscount ? true : false);
      setValue("weekdays", discount?.weekdays);
      setValue("discount", discount?.flatDiscount ?? discount?.discount);
      setValue(
        "endTime",
        getFormattedStringDate(
          new Date(
            discount?.endTime ||
              add(new Date(), {
                days: 1,
              })
          )
        )
      );
    }
  }, [discount]);

  function changeEndDateTime(runsForever: any) {
    if (runsForever) {
      setValue(
        "endTime",
        getFormattedStringDate(
          add(new Date(), {
            years: 100,
          })
        )
      );
      setSelectedDiscount({
        ...selectedDiscount,
        runsForever,
        endTime: new Date(
          add(new Date(), {
            years: 100,
          })
        ).getTime(),
      });
    } else {
      setValue(
        "endTime",
        getFormattedStringDate(
          add(new Date(), {
            days: 1,
          })
        )
      );
      setSelectedDiscount({
        ...selectedDiscount,
        runsForever,
        endTime: new Date(
          add(new Date(), {
            days: 1,
          })
        ).getTime(),
      });
    }
  }

  useEffect(() => {
    setAction(initialAction);
  }, [initialAction]);

  async function submitDiscount(values: any) {
    values.startTime = getBaseTimeStamp({ date: new Date(values.startTime) });
    if (selectedDiscount?.runsForever) {
      values.endTime = add(new Date(), { years: 100 });
    } else {
      values.endTime = new Date(values.endTime);
    }
    values.endTime = getBaseTimeStamp({ date: values.endTime });
    if (isFlatDiscount) {
      values.flatDiscount = parseInt(values.discount);
      values.discount = null;
    } else {
      values.discount = parseInt(values.discount);
      values.flatDiscount = null;
    }
    selectedDiscount = {
      ...selectedDiscount,
      ...values,
      testId: test ? test.id : "",
      categoryId: category ? category.id : "",
    };

    setSelectedDiscount({ ...selectedDiscount });
    await createDiscount(selectedDiscount);
    setSelectedDiscount(undefined);
    onClose?.();
  }

  function validateEndTime(value: any) {
    return value > watchStartTime;
  }

  async function submitDeleteDiscount(discount: Discount) {
    setAction("delete");
    setSelectedDiscount(discount);
  }

  function showWeekDays() {
    return (
      <Flex direction="column">
        <Controller
          render={(props) => (
            <Grid
              templateColumns="repeat(auto-fit, minmax(100px, 1fr))"
              wrap="wrap"
              gap={3}
            >
              {weekdays.map((weekday, index) => {
                index = index + 1;
                let selected: boolean =
                  selectedDiscount?.weekdays.includes(index) || false;

                return (
                  <Box
                    cursor="pointer"
                    padding="2px 4px"
                    border="2px solid"
                    borderColor={selected ? "primary.500" : "border.500"}
                    align="center"
                    borderRadius={4}
                    backgroundColor={selected ? "primary.500" : "white"}
                    color={selected ? "white" : "highlight.500"}
                    index={index}
                    key={index}
                    ref={props.ref}
                    onClick={() => {
                      if (selectedDiscount?.weekdays.includes(index)) {
                        selectedDiscount.weekdays =
                          selectedDiscount?.weekdays.filter(
                            (cat: any) => cat !== index
                          );
                      } else {
                        selectedDiscount?.weekdays.push(index);
                      }
                      props.onChange(selectedDiscount?.weekdays || []);
                      setSelectedDiscount({ ...selectedDiscount } as Discount);
                    }}
                  >
                    <div>{weekday}</div>
                  </Box>
                );
              })}
            </Grid>
          )}
          name="weekdays"
          control={control}
          rules={{
            required: true,
            validate: () => {
              if (selectedDiscount?.weekdays.length === 0) {
                return "At least one weekday should be selected";
              }
            },
          }}
          defaultValue={selectedDiscount?.weekdays}
        ></Controller>
        <ErrorMessage
          errors={errors}
          name="weekdays"
          as={<Text color={"error"} padding={1} fontSize="sm"></Text>}
        ></ErrorMessage>
      </Flex>
    );
  }

  function getMaxFlatDiscount(fee: number) {
    return Math.floor((90 / 100) * fee);
  }

  function getMaxFlatDiscountOfCategory(category: any, labTests: any) {
    const totalFee = getCategoryFee(category, labTests);
    return Math.floor((90 / 100) * totalFee);
  }

  function getNewDiscountForm() {
    return (
      <form onSubmit={handleSubmit(submitDiscount)}>
        {selectedDiscount && (
          <Flex direction="column">
            <AlertDialogHeader
              bg="modalHeader.500"
              color="white"
              borderTopRadius="4px"
            >
              {discount?.id ? "Update " : "Add "} Discount for{" "}
              {test?.name || category?.name || "Invalid Type"}
            </AlertDialogHeader>
            <AlertDialogCloseButton color="faded.500" fontSize="14" />

            <Stack direction="column" borderRadius={4} padding="8px 16px">
              <Flex justify="space-between">
                <Box padding={1} flex={2}>
                  <Text
                    fontSize="md"
                    paddingBottom={1}
                    color="highlight.500"
                    fontWeight="medium"
                  >
                    Discount Title
                  </Text>
                  <Input
                    type="text"
                    paddingY="6"
                    placeholder="Name of the discount"
                    borderRadius="4px"
                    defaultValue={selectedDiscount.name}
                    name="name"
                    ref={register}
                    required
                  />
                </Box>

                <Box padding={1} flex={1} marginLeft={6}>
                  <Flex justify="space-between">
                    <Text
                      fontSize="md"
                      paddingBottom={1}
                      color="highlight.500"
                      fontWeight="medium"
                    >
                      Discount {!isFlatDiscount ? "(In %)" : "(In Rs)"}
                    </Text>
                    <Checkbox
                      paddingBottom={1}
                      color="highlight.500"
                      fontSize="md"
                      colorScheme="primary"
                      defaultChecked={(discount?.flatDiscount ?? 0) > 0}
                      checked={(discount?.flatDiscount ?? 0) > 0}
                      onChange={(e) => setIsFlatDiscount(e.target.checked)}
                    >
                      Flat Discount
                    </Checkbox>
                  </Flex>
                  <Input
                    type="number"
                    paddingY="6"
                    borderRadius="4px"
                    placeholder="Enter discount"
                    value={selectedDiscount.discount}
                    defaultValue={selectedDiscount.discount}
                    max={
                      !isFlatDiscount
                        ? "90"
                        : test?.fee
                        ? getMaxFlatDiscount(test?.fee)
                        : getMaxFlatDiscountOfCategory(category, labTests?.data)
                    }
                    name="discount"
                    ref={register}
                    maxLength={
                      !isFlatDiscount
                        ? 2
                        : (
                            test?.fee ?? getCategoryFee(category, labTests.data)
                          ).toString().length
                    }
                    required
                  />
                </Box>
              </Flex>
              <Flex direction="column">
                <Flex direction="column">
                  <Text
                    fontSize="sm"
                    paddingBottom={1}
                    color={"highlight"}
                    fontWeight="medium"
                  >
                    Select Days
                  </Text>
                  {showWeekDays()}
                </Flex>

                <Flex align="center" justify="space-between" marginY={2}>
                  <Flex direction="column" justify="center" flex={1}>
                    <Text
                      fontSize="md"
                      color={"highlight"}
                      fontWeight="medium"
                      marginY="1"
                    >
                      From
                    </Text>
                    <Input
                      paddingY="6"
                      min={getFormattedStringDate(
                        new Date(new Date().getTime())
                      )}
                      type="date"
                      defaultValue={getFormattedStringDate(
                        new Date(selectedDiscount.startTime)
                      )}
                      placeholder="Start Date"
                      onChange={(e) =>
                        setSelectedDiscount({
                          ...selectedDiscount,
                          startTime: new Date(e.target.value),
                        })
                      }
                      name="startTime"
                      ref={register}
                      required
                    />
                  </Flex>
                  <Box margin={2}></Box>
                  <Flex direction="column" flex={1} justify="center">
                    <Flex justify="space-between">
                      <Text
                        fontSize="md"
                        color="#000"
                        fontWeight="medium"
                        marginY="1"
                      >
                        To
                      </Text>
                      <Checkbox
                        pt="2"
                        colorScheme="primary"
                        defaultChecked={selectedDiscount.runsForever}
                        checked={selectedDiscount?.runsForever}
                        onChange={(e) => changeEndDateTime(e.target.checked)}
                      >
                        Runs Forever
                      </Checkbox>
                    </Flex>
                    <Input
                      paddingY="6"
                      ref={register({
                        validate: validateEndTime,
                      })}
                      min={getFormattedStringDate(
                        add(new Date(selectedDiscount.startTime), {
                          days: 1,
                        })
                      )}
                      type="date"
                      placeholder="End Date"
                      name="endTime"
                      defaultValue={getFormattedStringDate(
                        new Date(selectedDiscount.endTime)
                      )}
                      disabled={selectedDiscount?.runsForever}
                      required
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Flex>
                <Button
                  type="submit"
                  isLoading={createDiscountInfo.isLoading}
                  margin="2"
                  paddingY="5"
                >
                  {discount?.id ? "Update " : "Add "} Discount
                </Button>
                <Button
                  margin="2"
                  onClick={() => {
                    onClose?.(selectedDiscount);
                  }}
                  background="white"
                  color="highlight.500"
                  border="solid 2px"
                  borderColor="faded.500"
                  paddingY="5"
                >
                  Cancel
                </Button>
              </Flex>
            </Stack>
          </Flex>
        )}
      </form>
    );
  }
  if (action === "delete" && selectedDiscount) {
    return (
      <Flex direction="column">
        <AlertDialogHeader bg="white" color="black" borderTopRadius="4px">
          Delete {selectedDiscount?.name}
        </AlertDialogHeader>
        <AlertDialogCloseButton color="richText.500" fontSize="14" />
        <Divider color="border.500" height="2px"></Divider>
        <AlertDialogBody marginY={4}>
          <Text size="sm" color="settingText.500">
            Are you sure you want to delete this test. Once you delete it you
            will not be able to get it back.
          </Text>
        </AlertDialogBody>
        <Divider></Divider>
        <AlertDialogFooter>
          <Button
            bg="red.300"
            onClick={async () => {
              await deleteDiscount(selectedDiscount);
              onClose?.();
            }}
            isLoading={deleteDiscountInfo.isLoading}
          >
            Delete
          </Button>
          <Button
            ml={3}
            variant="outline"
            border="2px"
            borderColor="border.500"
            onClick={() => {
              setAction(undefined);
              setSelectedDiscount(undefined);
            }}
          >
            Cancel
          </Button>
        </AlertDialogFooter>
      </Flex>
    );
  }
  return (
    <Flex direction="column">
      {!showDiscounts && getNewDiscountForm()}
      {showDiscounts && (
        <Flex direction="column">
          <AlertDialogHeader
            bg="modalHeader.500"
            color="white"
            borderTopRadius="4px"
          >
            Discounts for {test?.name || category?.name || "Invalid type"}
          </AlertDialogHeader>
          <AlertDialogCloseButton color="faded.500" fontSize="14" />

          <Flex direction="column" padding={"8px 16px"}>
            <Flex justify="space-between" align="center" marginY="1">
              <Text
                fontSize="md"
                color={"highlight"}
                fontWeight="medium"
                margin={0}
              >
                Upcoming Discounts
              </Text>
              {newDiscount && (
                <IconButton
                  size="md"
                  fontWeight="bold"
                  bg="transparent"
                  color="primary.500"
                  aria-label="add-discount"
                  icon={<AddIcon></AddIcon>}
                  onClick={() => {
                    newDiscount?.(selectedDiscount);
                  }}
                ></IconButton>
              )}
            </Flex>
            {discountsQuery.isLoading && <Text>Loading ... </Text>}{" "}
            {discountsQuery.data && discountsQuery.data.length === 0 && (
              <Text fontSize="sm" padding={1} color="settingText.500">
                No discounts
              </Text>
            )}
            {discountsQuery.data && (
              <Flex direction="column">
                {discountsQuery.data.map((discount: any) => {
                  return (
                    <DiscountCard
                      key={discount.id}
                      discount={discount}
                      editDiscount={editDiscount}
                      deleteDiscount={submitDeleteDiscount}
                      showEdit={true}
                    ></DiscountCard>
                  );
                })}
              </Flex>
            )}
            <Flex justify="flex-end" marginY="2">
              <Button
                margin="2"
                onClick={() => {
                  onClose?.(selectedDiscount);
                }}
                background="white"
                color="highlight.500"
                border="solid 2px"
                borderColor="faded.500"
                paddingY="5"
              >
                Close
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export function DiscountCard({
  discount,
  editDiscount,
  deleteDiscount,
  showEdit = true,
}: {
  discount: Discount;
  editDiscount?: Function | undefined;
  deleteDiscount: Function;
  showEdit: boolean;
}) {
  let status = discount.getStatus();
  return (
    <Flex
      direction="column"
      border="2px solid"
      borderColor="border.500"
      borderRadius="md"
      margin={1}
      padding="4px 8px"
      position="relative"
    >
      <Flex justify="space-between">
        <Text fontWeight="medium" fontSize="md" color={"highlight"}>
          {discount.name}
        </Text>
        <Box position="absolute" zIndex={100} top={2} right={2}>
          <Flex>
            <Box
              onClick={() => deleteDiscount(discount)}
              cursor="pointer"
              padding={1}
            >
              <DeleteIcon height={4} width={4} color={"error"} />
            </Box>
            {showEdit ? (
              <Box
                cursor="pointer"
                padding={1}
                onClick={() => {
                  if (editDiscount) editDiscount(discount);
                }}
              >
                <EditIcon height={4} width={4} />
              </Box>
            ) : null}
          </Flex>
        </Box>
      </Flex>
      <Text fontWeight="medium" fontSize="sm" color="settingText">
        {discount.discount
          ? `${discount.discount} % `
          : `${discount.flatDiscount} Rs`}
      </Text>
      <Flex justify="space-between">
        <Flex>
          <Text fontWeight="medium" fontSize="sm" color="settingText">
            {getFormattedStringDate(new Date(discount.startTime))}
          </Text>
          <Text
            fontWeight="medium"
            fontSize="sm"
            color="settingText"
            paddingLeft={2}
            paddingRight={2}
          >
            to
          </Text>
          <Text fontWeight="medium" fontSize="sm" color={"settingText"}>
            {getFormattedStringDate(new Date(discount.endTime))}
          </Text>
        </Flex>
        <Text
          paddingRight={1}
          size="sm"
          fontWeight="bold"
          color={
            status === "active"
              ? "primary.500"
              : status === "expired"
              ? "red.300"
              : "appointmentYellow.500"
          }
        >
          {status.toUpperCase()}
        </Text>
      </Flex>
    </Flex>
  );
}
